/**
 * Templates page component with tab navigation
 */
import { Box, Paper, Tab, Tabs, Typography } from '@mui/material';
import React, { useState } from 'react';

import { useRegisterPageHelp } from '@/hooks/useRegisterPageHelp';
import LayersTemplatesList from '../components/Templates/LayersTemplatesList';
import TemplatesList from '../components/Templates/TemplatesList';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`templates-tabpanel-${index}`}
      aria-labelledby={`templates-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const TEMPLATE_TAB_LABELS = ['Reservoir Templates', 'Economy Templates'] as const;

export const TemplatesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  useRegisterPageHelp('Templates', TEMPLATE_TAB_LABELS[activeTab] ?? null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Templates
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Manage your calculation templates for different entities
        </Typography>

        <Box sx={{
          display: 'flex',
          gap: 1,
          mb: 3,
          borderBottom: 'none' // Remove default line under tabs
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="templates tabs"
            sx={{
              '& .MuiTabs-indicator': {
                display: 'none', // Remove indicator
              },
              '& .MuiTab-root': {
                minHeight: 40,
                borderRadius: 1,
                mx: 0.5,
                px: 2,
                py: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 400,
                border: 1,
                borderColor: 'divider',
                backgroundColor: 'background.paper',
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderColor: 'primary.main',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                },
              },
            }}
          >
            <Tab label="Reservoir Templates" id="templates-tab-0" aria-controls="templates-tabpanel-0" />
            <Tab label="Economy Templates" id="templates-tab-1" aria-controls="templates-tabpanel-1" />
          </Tabs>
        </Box>
      </Paper>

      <TabPanel value={activeTab} index={0}>
        <Typography variant="h6" gutterBottom>
          Reservoir Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Templates for geological reservoirs with predefined properties
        </Typography>
        <LayersTemplatesList />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <Typography variant="h6" gutterBottom>
          Economy Templates
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Field-level and layer-level unit economics presets with currency and production units
        </Typography>
        <TemplatesList />
      </TabPanel>
    </Box>
  );
};

export default TemplatesPage;
